import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, Form, Button, Row, Col, Table, Image, Spinner,
} from 'react-bootstrap';
import _ from 'lodash';
import Error from '../../../Components/Error';
import FormControlFeedback from '../../../Components/FormControlFeedback';
import setStateFromInput from '../../../Utils/setStateFromInput';
import PricingService from '../../../Services/PricingService';

const defaultBand = {
  up_to_km: 0,
  flat_rate_eur: '',
  flat_rate_huf: '',
  per_km_eur: '',
  per_km_huf: '',
};

function VehicleCategoryModal({
  originalCategory = {}, show = false, handleClose, fetchCategories, allCategories = [],
}) {
  const defaultCategory = {
    active: 1,
    display_order: 0,
    pricing: {
      bands: [
        {
          up_to_km: 15, flat_rate_eur: 40, flat_rate_huf: 16000,
        },
        {
          up_to_km: 40, per_km_eur: 1.15, per_km_huf: 460,
        },
        {
          up_to_km: 150, per_km_eur: 0.75, per_km_huf: 300,
        },
        {
          up_to_km: 0, per_km_eur: 0.85, per_km_huf: 340,
        },
      ],
      hourly: null,
    },
  };

  const merged = _.defaults(_.cloneDeep(originalCategory), defaultCategory);
  const [category, setCategory] = useState(merged);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  // Reset state when originalCategory changes
  useEffect(() => {
    setCategory(_.defaults(_.cloneDeep(originalCategory), defaultCategory));
    setImagePreview(originalCategory?.image_url || null);
    setError('');
    setValidationErrors([]);
  }, [originalCategory]);

  const handleSubmit = async () => {
    setLoading(true);

    try {
      // Convert bands to proper numbers
      const bands = (category.pricing?.bands || []).map((band) => ({
        up_to_km: Number(band.up_to_km || 0),
        flat_rate_eur: band.flat_rate_eur ? Number(band.flat_rate_eur) : undefined,
        flat_rate_huf: band.flat_rate_huf ? Number(band.flat_rate_huf) : undefined,
        per_km_eur: band.per_km_eur ? Number(band.per_km_eur) : undefined,
        per_km_huf: band.per_km_huf ? Number(band.per_km_huf) : undefined,
      }));

      // Build hourly pricing if any values are set
      const hourlyPricing = category.pricing?.hourly;
      let hourly;
      if (hourlyPricing?.hourly_rate_eur || hourlyPricing?.hourly_rate_huf) {
        // Build waiting time pricing (nested under hourly)
        const waitingTimePricing = hourlyPricing?.waiting_time;
        const waitingTime = (waitingTimePricing?.per_hour_eur || waitingTimePricing?.per_hour_huf)
          ? {
            per_hour_eur: Number(waitingTimePricing.per_hour_eur || 0),
            per_hour_huf: Number(waitingTimePricing.per_hour_huf || 0),
          }
          : undefined;

        hourly = {
          hourly_rate_eur: Number(hourlyPricing.hourly_rate_eur || 0),
          hourly_rate_huf: Number(hourlyPricing.hourly_rate_huf || 0),
          minimum_hours: Number(hourlyPricing.minimum_hours || 1),
          waiting_time: waitingTime,
        };
      }

      await PricingService.createOrUpdateVehicleCategory({
        ...category,
        active: Number(category.active),
        capacity: Number(category.capacity),
        max_luggage: Number(category.max_luggage),
        display_order: Number(category.display_order),
        pricing: {
          bands,
          hourly,
        },
      });
      fetchCategories();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message);
      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setStateFromInput(e, setCategory, category);
  };

  const handleUpgradableToChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, (opt) => opt.value);
    setCategory((prev) => ({ ...prev, upgradable_to: selected }));
  };

  // Other active categories the current one can be upgraded to (exclude self).
  const upgradeTargetOptions = (allCategories || [])
    .filter((c) => c.active === 1 && c.Id !== category.Id);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only PNG, JPEG, and WebP images are allowed');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be less than 2MB');
      return;
    }

    // Category must be saved first to upload image
    if (!category.Id) {
      setError('Please save the category first before uploading an image');
      return;
    }

    setImageLoading(true);
    setError('');

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          // Show local preview immediately
          setImagePreview(event.target.result);

          const base64Data = event.target.result.split(',')[1]; // Remove data:image/xxx;base64, prefix
          const result = await PricingService.uploadVehicleCategoryImage(
            category.Id,
            base64Data,
            file.type,
          );
          // Update with the actual S3 presigned URL and image key
          const responseData = result.data?.data || result.data;
          setImagePreview(responseData.image_url);
          setCategory((prev) => ({ ...prev, image_key: responseData.image_key }));
        } catch (err) {
          setError(err.response?.data?.message || 'Failed to upload image');
          // Revert preview on error
          setImagePreview(originalCategory?.image_url || null);
        } finally {
          setImageLoading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError('Failed to read image file');
      setImageLoading(false);
    }
  };

  const handleRemoveImage = async () => {
    if (!category.Id) return;

    setImageLoading(true);
    setError('');

    try {
      await PricingService.removeVehicleCategoryImage(category.Id);
      setImagePreview(null);
      setCategory((prev) => ({ ...prev, image_key: undefined }));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove image');
    } finally {
      setImageLoading(false);
    }
  };

  const handleBandChange = (index, field, value) => {
    setCategory((prev) => {
      const updatedBands = [...(prev.pricing?.bands || [])];
      updatedBands[index] = {
        ...updatedBands[index],
        [field]: value,
      };
      return {
        ...prev,
        pricing: { ...prev.pricing, bands: updatedBands },
      };
    });
  };

  const handleHourlyChange = (field, value) => {
    setCategory((prev) => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        hourly: {
          ...(prev.pricing?.hourly || {}),
          [field]: value,
        },
      },
    }));
  };

  const handleWaitingTimeChange = (field, value) => {
    setCategory((prev) => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        hourly: {
          ...(prev.pricing?.hourly || {}),
          waiting_time: {
            ...(prev.pricing?.hourly?.waiting_time || {}),
            [field]: value,
          },
        },
      },
    }));
  };

  const addBand = () => {
    setCategory((prev) => {
      const bands = [...(prev.pricing?.bands || [])];
      // Insert new band before the last one (unlimited band with up_to_km = 0)
      const lastBand = bands.find((b) => b.up_to_km === 0);
      if (lastBand) {
        const insertIndex = bands.indexOf(lastBand);
        bands.splice(insertIndex, 0, { ...defaultBand, up_to_km: 100 });
      } else {
        bands.push({ ...defaultBand });
      }
      return {
        ...prev,
        pricing: { ...prev.pricing, bands },
      };
    });
  };

  const removeBand = (index) => {
    setCategory((prev) => {
      const bands = [...(prev.pricing?.bands || [])];
      bands.splice(index, 1);
      return {
        ...prev,
        pricing: { ...prev.pricing, bands },
      };
    });
  };

  const bands = category.pricing?.bands || [];

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {category.Id ? `Edit ${category.name}` : 'New Vehicle Category'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Error error={error} setError={setError} />

        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="name">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={category.name || ''}
                  onChange={handleChange}
                  placeholder="e.g., Standard Sedan"
                />
                <FormControlFeedback errors={validationErrors} field="name" />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3" controlId="capacity">
                <Form.Label>Passenger Capacity *</Form.Label>
                <Form.Control
                  type="number"
                  value={category.capacity || ''}
                  onChange={handleChange}
                  min={1}
                />
                <FormControlFeedback errors={validationErrors} field="capacity" />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3" controlId="max_luggage">
                <Form.Label>Max Luggage *</Form.Label>
                <Form.Control
                  type="number"
                  value={category.max_luggage || ''}
                  onChange={handleChange}
                  min={0}
                />
                <FormControlFeedback errors={validationErrors} field="max_luggage" />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="description">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={category.description || ''}
                  onChange={handleChange}
                  placeholder="Optional description"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3" controlId="display_order">
                <Form.Label>Display Order</Form.Label>
                <Form.Control
                  type="number"
                  value={category.display_order || 0}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3" controlId="active">
                <Form.Label>Status</Form.Label>
                <Form.Select value={category.active} onChange={handleChange}>
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3" controlId="upgradable_to">
                <Form.Label>Upgradable To</Form.Label>
                <Form.Select
                  multiple
                  value={category.upgradable_to || []}
                  onChange={handleUpgradableToChange}
                  htmlSize={Math.min(Math.max(upgradeTargetOptions.length, 3), 6)}
                >
                  {upgradeTargetOptions.map((c) => (
                    <option key={c.Id} value={c.Id}>
                      {c.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Categories customers can upgrade to (48h-before offer). Hold Ctrl/Cmd to select
                  multiple. Only active categories are listed.
                </Form.Text>
                <FormControlFeedback errors={validationErrors} field="upgradable_to" />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Category Image</Form.Label>
                <div className="d-flex align-items-center gap-3">
                  {imagePreview ? (
                    <div className="position-relative">
                      <Image
                        src={imagePreview}
                        alt="Category"
                        style={{ maxHeight: '100px', maxWidth: '150px', objectFit: 'contain' }}
                        thumbnail
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        className="position-absolute top-0 end-0"
                        onClick={handleRemoveImage}
                        disabled={imageLoading}
                        style={{ transform: 'translate(50%, -50%)' }}
                      >
                        <i className="fa fa-times" />
                      </Button>
                    </div>
                  ) : (
                    <div
                      className="border rounded p-3 text-center text-muted"
                      style={{
                        width: '150px', height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      No image
                    </div>
                  )}
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      style={{ display: 'none' }}
                    />
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={imageLoading || !category.Id}
                    >
                      {imageLoading ? (
                        <>
                          <Spinner size="sm" className="me-1" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-upload me-1" />
                          Upload Image
                        </>
                      )}
                    </Button>
                    {!category.Id && (
                      <Form.Text className="text-muted d-block mt-1">
                        Save the category first to upload an image
                      </Form.Text>
                    )}
                    <Form.Text className="text-muted d-block">
                      PNG, JPEG, or WebP. Max 2MB.
                    </Form.Text>
                  </div>
                </div>
              </Form.Group>
            </Col>
          </Row>

          <hr />
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Distance-Based Pricing Bands</h5>
            <Button variant="outline-primary" size="sm" onClick={addBand}>
              <i className="fa fa-plus me-1" />
              Add Band
            </Button>
          </div>

          <p className="text-muted small mb-3">
            Configure pricing bands based on distance. Use either a flat rate OR per-km rate for
            each band. Set &quot;Up to KM&quot; to 0 for the final unlimited band.
          </p>

          <Table responsive bordered size="sm">
            <thead className="table-light">
              <tr>
                <th style={{ width: '100px' }}>Up to KM</th>
                <th>Flat Rate (EUR)</th>
                <th>Flat Rate (HUF)</th>
                <th>Per KM (EUR)</th>
                <th>Per KM (HUF)</th>
                <th style={{ width: '60px' }} />
              </tr>
            </thead>
            <tbody>
              {bands.map((band, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={`band-${index}`}>
                  <td>
                    <Form.Control
                      type="number"
                      size="sm"
                      value={band.up_to_km || ''}
                      onChange={(e) => handleBandChange(index, 'up_to_km', e.target.value)}
                      placeholder={band.up_to_km === 0 ? '0 (unlimited)' : ''}
                      min={0}
                    />
                    {band.up_to_km === 0 && (
                      <small className="text-muted">Unlimited</small>
                    )}
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      size="sm"
                      step="0.01"
                      value={band.flat_rate_eur || ''}
                      onChange={(e) => handleBandChange(index, 'flat_rate_eur', e.target.value)}
                      placeholder="—"
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      size="sm"
                      step="1"
                      value={band.flat_rate_huf || ''}
                      onChange={(e) => handleBandChange(index, 'flat_rate_huf', e.target.value)}
                      placeholder="—"
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      size="sm"
                      step="0.01"
                      value={band.per_km_eur || ''}
                      onChange={(e) => handleBandChange(index, 'per_km_eur', e.target.value)}
                      placeholder="—"
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      size="sm"
                      step="1"
                      value={band.per_km_huf || ''}
                      onChange={(e) => handleBandChange(index, 'per_km_huf', e.target.value)}
                      placeholder="—"
                    />
                  </td>
                  <td className="text-center">
                    {bands.length > 1 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeBand(index)}
                        title="Remove band"
                      >
                        <i className="fa fa-trash" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="alert alert-info small">
            <strong>Example:</strong>
            {' '}
            For Standard vehicle: First 15km = 40 EUR (flat), 15-40km = 1.15 EUR/km,
            40-150km = 0.75 EUR/km, After 150km = 0.85 EUR/km
          </div>

          <hr />
          <h5 className="mb-3">Hourly Rental Pricing (Optional)</h5>
          <p className="text-muted small mb-3">
            Configure pricing for hourly rentals within a base location. Leave empty if not offering
            hourly rentals for this vehicle category.
          </p>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Hourly Rate (EUR)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={category.pricing?.hourly?.hourly_rate_eur || ''}
                  onChange={(e) => handleHourlyChange('hourly_rate_eur', e.target.value)}
                  placeholder="e.g., 35"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Hourly Rate (HUF)</Form.Label>
                <Form.Control
                  type="number"
                  step="1"
                  value={category.pricing?.hourly?.hourly_rate_huf || ''}
                  onChange={(e) => handleHourlyChange('hourly_rate_huf', e.target.value)}
                  placeholder="e.g., 14000"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Minimum Hours</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={category.pricing?.hourly?.minimum_hours || ''}
                  onChange={(e) => handleHourlyChange('minimum_hours', e.target.value)}
                  placeholder="e.g., 3"
                />
                <Form.Text className="text-muted">
                  Minimum booking duration for hourly rentals
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <h6 className="mb-2 mt-3">Waiting Time (Optional)</h6>
          <p className="text-muted small mb-3">
            Charge for waiting time when the vehicle is stationary during hourly rentals.
          </p>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Per Hour (EUR)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={category.pricing?.hourly?.waiting_time?.per_hour_eur || ''}
                  onChange={(e) => handleWaitingTimeChange('per_hour_eur', e.target.value)}
                  placeholder="e.g., 25"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Per Hour (HUF)</Form.Label>
                <Form.Control
                  type="number"
                  step="1"
                  value={category.pricing?.hourly?.waiting_time?.per_hour_huf || ''}
                  onChange={(e) => handleWaitingTimeChange('per_hour_huf', e.target.value)}
                  placeholder="e.g., 10000"
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? 'Saving...' : 'Save Category'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default VehicleCategoryModal;
